import { ReactNode } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronLeftIcon } from '@/components/Icons';
import styles from './Accordion.module.css';

export interface AccordionItemProps {
  value: string;
  trigger: ReactNode;
  children: ReactNode;
  disabled?: boolean;
}

interface BaseAccordionProps {
  items: AccordionItemProps[];
  disabled?: boolean;
  className?: string;
}

interface SingleAccordionProps extends BaseAccordionProps {
  type: 'single';
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
}

interface MultipleAccordionProps extends BaseAccordionProps {
  type: 'multiple';
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
}

type AccordionProps = SingleAccordionProps | MultipleAccordionProps;

export default function CustomAccordion(props: AccordionProps) {
  const { items, disabled = false, className, type = 'single' } = props;
  const combinedClassName = `${styles.accordion} ${className || ''}`.trim();

  if (type === 'multiple') {
    const { defaultValue, value, onValueChange } = props as MultipleAccordionProps;
    return (
      <Accordion.Root
        type="multiple"
        defaultValue={defaultValue}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        className={combinedClassName}
      >
        {items.map((item) => (
          <Accordion.Item
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={styles.accordionItem}
          >
            <Accordion.Header className={styles.accordionHeader}>
              <Accordion.Trigger className={styles.accordionTrigger}>
                <span className={styles.triggerText}>{item.trigger}</span>
                <ChevronLeftIcon size={20} color="text" className={styles.chevron} />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className={styles.accordionContent}>
              <div className={styles.accordionContentInner}>
                {item.children}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    );
  }

  const { defaultValue, value, onValueChange, collapsible = true } = props as SingleAccordionProps;
  return (
    <Accordion.Root
      type="single"
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      collapsible={collapsible}
      disabled={disabled}
      className={combinedClassName}
    >
      {items.map((item) => (
        <Accordion.Item
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          className={styles.accordionItem}
        >
          <Accordion.Header className={styles.accordionHeader}>
            <Accordion.Trigger className={styles.accordionTrigger}>
              <span className={styles.triggerText}>{item.trigger}</span>
              <ChevronLeftIcon size={20} color="text" className={styles.chevron} />
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className={styles.accordionContent}>
            <div className={styles.accordionContentInner}>
              {item.children}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}